import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import {
  CollectionDetailsDTO,
  CollectionFinderItemDTO,
  CollectionManagerItemDTO,
  CollectionSaveDTO,
} from '../../interfaces/collection.interface';
import { catchError, Observable, retry, take, throwError } from 'rxjs';
import { AppInterface } from '../../interfaces/app.interface';
import { ErrorSignalService } from '../../components/error-page/services/error-signal.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class CollectionApiService {
  private collectionsEndpoint = environment.baseUrl + '/collections';
  private collectionInstancesEndpoint = environment.baseUrl + '/me/collection-instances';
  private collectionFinderEndpoint = environment.baseUrl + '/me/collectionFinder';
  private collectionManagerEndpoint = environment.baseUrl + '/collectionManager';

  constructor(
    private http: HttpClient,
    private router: Router,
    private errorSignalService: ErrorSignalService,
  ) {}

  public listFinder(getAll: boolean = false, getTranslations = false): Observable<CollectionFinderItemDTO[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('getAll', getAll);
    if (getTranslations) {
      params = params.set('getTranslations', true);
    }
    return this.http.get<CollectionFinderItemDTO[]>(this.collectionFinderEndpoint, { params }).pipe(
      take(1),
      retry(3), // retry transient failures
      catchError((error) => {
        console.error(
          'Error fetching active collections that can be added to the user start page:',
          error,
        );
        return throwError(
          () => new Error('Sorry, we couldn’t load your start page apps. Please try again later.'),
        );
      }),
    );
  }

  public listManager(): Observable<CollectionManagerItemDTO[]> {
    return this.http.get<CollectionManagerItemDTO[]>(this.collectionManagerEndpoint).pipe(
      take(1),
      retry(3), // retry transient failures
      catchError((error) => {
        if (error.status !== 401) {
          // for 401 do nothing - sso will fix it automatically
          console.error('Error fetching collections from Manager API:', error);
          this.errorSignalService.triggerAppManagerBanner();
        }
        return throwError(
          () => new Error('Sorry, we couldn’t load your collections. Please try again later.'),
        );
      }),
    );
  }

  public details(_id: number): Observable<CollectionDetailsDTO> {
    return this.http.get<CollectionDetailsDTO>(`${this.collectionsEndpoint}/${_id}`).pipe(
      take(1),
      retry(3), // retry transient failures
      catchError((error) => {
        console.error('Error fetching collection details:', error);

        if (error.status === 404) {
          // Redirect to a not-found route
          this.router.navigate(['error/404']);
        } else if (error.status === 401) {
          // we do nothing - sso will fix it automatically
        } else {
          // Trigger Collection Details banner for other errors
          this.errorSignalService.triggerCollectionDetailsBanner();
        }

        return throwError(
          () =>
            new Error('Sorry, we couldn’t load the collection details. Please try again later.'),
        );
      }),
    );
  }

  public getCollectionApps(_collectionId: number): Observable<AppInterface[]> {
    return this.http
      .get<AppInterface[]>(`${this.collectionsEndpoint}/${_collectionId}/apps`)
      .pipe(take(1));
  }

  public create(_collection: CollectionSaveDTO): Observable<CollectionDetailsDTO> {
    return this.http
      .post<CollectionDetailsDTO>(this.collectionsEndpoint, _collection)
      .pipe(take(1));
  }

  public update(_id: number, _data: CollectionSaveDTO): Observable<CollectionDetailsDTO> {
    return this.http
      .put<CollectionDetailsDTO>(`${this.collectionsEndpoint}/${_id}`, _data)
      .pipe(take(1));
  }

  public deleteCollection(_id: number): Observable<void> {
    return this.http.delete<void>(`${this.collectionsEndpoint}/${_id}`).pipe(take(1));
  }

  public addCollection(_id: number): Observable<void> {
    return this.http
      .post<void>(`${this.collectionInstancesEndpoint}/${_id}`, undefined)
      .pipe(take(1));
  }

  public removeCollection(_id: number): Observable<void> {
    return this.http.delete<void>(`${this.collectionInstancesEndpoint}/${_id}`).pipe(take(1));
  }

  public getUserAddedCollectionApps(): Observable<number[]> {
    return this.http.get<number[]>(`${this.collectionInstancesEndpoint}/apps`).pipe(take(1));
  }

  public getAddedCollectionsForCurrentUser(): Observable<CollectionDetailsDTO[]> {
    return this.http.get<CollectionDetailsDTO[]>(`${this.collectionInstancesEndpoint}`).pipe(
      take(1),
      retry(3), // retry transient failures
      catchError((error) => {
        console.error('Error fetching user-added collection apps:', error);
        return throwError(
          () => new Error('Sorry, we couldn’t load your start page apps. Please try again later.'),
        );
      }),
    );
  }
}
